import { fileSchema } from '../File/schemas';
import { formSchema } from '../Form/schemas';
import { createListApiSchema } from '../General/schemas';
import { groupSchema } from '../Group/schemas';
import { roleSchema } from '../Role/schemas';
import {
	accessAppSettingSchema,
	userDataSettingSchema,
	customerThemeSettingSchema,
	languagesSettingSchema,
} from '../Settings/schemas';
import { taskSchema } from '../Task/schemas';
import { zoneSchema } from '../Zone/schemas';
import { siteSchema } from '../Site/schemas';
import z from 'zod';

export const lastKnownLocationSchema = z.object({
	siteId: z.string(),
	siteName: z.string().optional(),
	zoneId: z.string().optional().nullable(),
	zoneName: z.string().optional().nullable(),
	timestamp: z.coerce.date(),
});

export const UserFromAuthenticationApiSchema = z.object({
	groups: z.array(z.string()),
	firstName: z.string(),
	lastName: z.string(),
});

export const userSchema = z
	.object({
		id: z.string(),
		shortId: z.string(),
		firstName: z.string().optional().nullable(),
		lastName: z.string().optional().nullable(),
		status: z.enum(['active', 'blocked', 'archived']).default('active'),
		authMethods: z
			.array(
				z.object({
					type: z.string(),
					id: z.string(),
					params: z.object({
						password: z.string(),
					}),
				}),
			)
			.optional(),
		dateOfBirth: z.coerce.date().optional(),
		phone: z.string().optional().nullable(),
		email: z.string().optional().nullable(),
		dispatcherRegistrationEmail: z.string().optional().nullable(),
		documents: z.array(
			z.object({
				mimeType: z.string(),
				fileName: z.string(),
				status: z.enum(['accepted', 'inReview', 'pending', 'declined', 'expired', 'expiryWarning', 'superseded']),
				bytes: z.number(),
				type: z.enum(['generic']),
				createdAt: z.coerce.date(),
				updatedAt: z.coerce.date(),
				link: z.string(),
			}),
		),
		facePrintRSIDInitial: z.any().optional(),
		facePrintRSID: z.any().optional(),
		facePrintType: z.enum(['none', 'initial', 'verified', 'failed']).optional(),
		twoFactorSecret: z.string().optional(),
		authSecret: z.string().optional(),
		userData: z
			.object({
				email: z.string().optional().nullable(),
				firstName: z.string().optional().nullable(),
				lastName: z.string().optional().nullable(),
				phone: z.string().optional().nullable(),
				idCardNumber: z.string().optional().nullable(),
				documentNumber: z.string().optional().nullable(),
				dateOfBirth: z.coerce.date().optional().nullable(),
				countryName: z.string().optional().nullable(),
				countryCode: z.string().optional().nullable(),
				inlineEditing: z.boolean().optional(),
				fixedPhoneLine: z.string().optional().nullable(),
				// Custom user properties live nested here, keyed by their defined name.
				// Tolerant on purpose: prod holds string, boolean and null values, and the
				// set of keys is tenant-defined, so we accept any key/value rather than a
				// whitelist that would strip off-list values on parse.
				extraUserProps: z.record(z.string(), z.unknown()).optional(),
			})
			// userData also carries platform-managed fields (ID-card / import / integration
			// metadata) that aren't all enumerated above; catchall keeps them from being
			// stripped if userData is ever parsed.
			.catchall(z.unknown())
			.optional(),
		workforceId: z.string().optional(),
		groupIds: z.array(z.string()),
		groups: z.array(z.lazy(() => groupSchema)).optional(),
		badgeId: z.string().optional(),
		tempAccessRights: z
			.array(
				z.object({
					from: z.coerce.date(),
					to: z.coerce.date(),
					zoneIds: z.array(z.string()),
				}),
			)
			.optional(),
		roles: z.array(
			z.object({
				status: z.enum(['pending', 'accepted', 'expired', 'expiryWarning', 'archived']),
				name: z.string(),
				id: z.string(),
				expiryWarningSentAt: z.coerce.date().optional(),
				validTo: z.coerce.date().optional(),
				expiryWarningShouldBeSent: z.boolean().optional(),
			}),
		),
		requirements: z.array(
			z.object({
				key: z.string(),
				name: z.string(),
				status: z.enum(['todo', 'pending', 'failed', 'accepted', 'expired', 'expiryWarning']),
				expiryDate: z.coerce.date().optional(),
				data: z.any(),
			}),
		),
		keywords: z.string().optional(),
		loginAttempts: z.number().optional(),
		lastLoginAttempt: z.coerce.date().optional(),
		externalId: z.string().optional(),
		last2FAReminder: z.coerce.date().optional(),
		language: z.string().optional(),
		// Two-axis model: `language` = which translations (2-letter, e.g. `en`);
		// `locale` = regional formatting (BCP-47, e.g. `nl-BE`).
		// Per-user BCP-47 locale/format override (9ID-1878 Part 3).
		// Null/absent = "Automatic" (use the resolveLocale derivation chain).
		locale: z.string().nullable().optional(),
		archivedAt: z.coerce.date().nullable().optional(),
		plannedArchival: z
			.object({
				archiveDate: z.coerce.date().nullable().optional(),
				archiveRoles: z.boolean().optional(),
				actor: z.object({
					id: z.string(),
					firstName: z.string(),
					lastName: z.string(),
				}),
			})
			.optional(),
		markedForReview: z.any().optional(),
		markedForReviewOverride: z.array(z.string()).optional(),
		twoFactor: z.boolean().optional(),
		hasTwoFactor: z.boolean().optional(), // Added in method `getWithPermissions`
		lastUserActivityAt: z.coerce.date().optional(),
		populatedRoles: z.array(z.lazy(() => roleSchema)).optional(),
		personalInfo: z.string().optional(),
		readOnly: z.boolean().optional(),
		supervisorId: z.string().optional(),
		supervisor: z
			.lazy(() => userSchema)
			.optional()
			.nullable(),
		lastKnownLocation: lastKnownLocationSchema.optional().nullable(),
	})
	.describe('User');

export const usersApiResponseSchema = createListApiSchema(userSchema).describe('Users');

export const userWithPermissionsSchema = userSchema
	.extend({
		permissions: z.array(z.string()),
	})
	.describe('UserWithPermissions');

export const loginApiResponseSchema = z.object({
	sessionUser: z.lazy(() => userWithPermissionsSchema),
});

export const authenticationSessionSchema = z
	.object({
		user: userWithPermissionsSchema.omit({
			shortId: true,
			status: true,
			documents: true,
			roles: true,
			requirements: true,
		}),
		ssoAvailable: z.boolean(),
	})
	.describe('AuthenticationSession');

export const authenticatedUserSchema = z
	.object({
		id: userSchema.shape.id,
		firstName: userSchema.shape.firstName,
		lastName: userSchema.shape.lastName,
		email: userSchema.shape.email,
		language: userSchema.shape.language,
		// Per-user locale/format override surfaced in the session payload (9ID-1878 Part 3).
		locale: userSchema.shape.locale,
		permissions: z.array(z.string()),
		roles: z.array(z.string()),
		groupIds: userSchema.shape.groupIds,
		twoFactor: z.boolean(),
		// TODO: Remove this if we ever remove this property as it's kinda dirty.
		isSystemAdmin: z.boolean().optional(),
	})
	.describe('AuthenticatedUser');

export const meSchema = z
	.object({
		user: userSchema.pick({
			id: true,
			email: true,
			firstName: true,
			lastName: true,
			language: true,
		}),
	})
	.describe('Me');

export const userOverviewSchema = z
	.object({
		openTasks: z.array(z.lazy(() => taskSchema)),
		user: userSchema.extend({ decryptedAuthSecret: z.string().nullable() }),
		customerName: z.string(),
		autoCheckin: z.boolean(),
		markedForReview: z.boolean(),
		showAccessCode: z.boolean(),
		showManagerAccess: z.boolean(),
		accessCode: z.string(),
		accessTokenSecurity: z.string(),
		accessAppSettings: z.lazy(() => accessAppSettingSchema.shape.settings).nullable(),
	})
	.describe('UserOverview');

export const accessAppOverviewSchema = z
	.object({
		rolesWithTasks: z.any(),
		user: userSchema.extend({ decryptedAuthSecret: z.string().nullable() }),
		site: z.lazy(() => siteSchema),
		markedForReview: z.boolean(),
		hasInvalidGroup: z.boolean(),
		showQRCode: z.boolean(),
		showManagerAccess: z.boolean(),
		accessCode: z.string(),
		accessTokenSecurity: z.string(),
		accessAppSettings: z.lazy(() => accessAppSettingSchema.shape.settings).nullable(),
		themeSettings: z.lazy(() => customerThemeSettingSchema.shape.settings),
		languagesSettings: z.lazy(() => languagesSettingSchema.shape.settings),
	})
	.describe('AccessAppOverviewSchema');

export const contactSchema = z
	.object({
		id: z.string(),
		label: z.string(),
		keywords: z.string(),
	})
	.describe('Contact');

export const contactsApiResponseSchema = createListApiSchema(contactSchema).describe('Contacts');

export const profileSchema = z
	.object({
		user: userWithPermissionsSchema,
		userData: userDataSettingSchema,
		userForm: z.lazy(() => formSchema).nullable(),
		faceImage: z
			.lazy(() => fileSchema)
			.optional()
			.nullable(),
	})
	.describe('Profile');

export const siteAttendeeSchema = z.object({
	id: z.string(),
	firstName: z.string(),
	lastName: z.string(),
	timestamp: z.coerce.date(),
	zoneName: z.string().optional(),
	groupNames: z.array(z.string()).optional(),
	roles: z.array(z.lazy(() => roleSchema)).optional(),
	comp: z.string().optional(),
	deviceName: z.string().optional(),
	eventName: z.string().optional(),
	direction: z.any().optional(),
	status: z.any().optional(),
	error: z.any().optional(),
	markedForReview: z.boolean().optional(),
	userId: z.string().optional(),
	siteId: z.string().optional(),
	zoneId: z.string().optional(),
	ciawStatus: z.string().nullable().optional(),
	ciawRemarks: z.array(z.string()).optional(),
	ciawId: z.string().nullable().optional(),
});

export const siteAttendeesApiResponseSchema = createListApiSchema(siteAttendeeSchema).describe('SiteAttendees');

export const selectedSiteIdSchema = z.object({
	selectedSiteId: z.string(),
});

export const userAccessSchema = z.object({
	id: z.string(),
	hasAccess: z.boolean(),
	reason: z.string(),
	accessType: z.enum(['permanent', 'event', 'schedule', 'single']),
	eventsAvailable: z.array(
		z.object({
			id: z.string(),
			name: z.string(),
		}),
	),
	zone: z.lazy(() => zoneSchema),
	roles: z.array(z.lazy(() => roleSchema)),
	schedule: z
		.object({
			day: z.string(),
			times: z.array(
				z.object({
					startTime: z.string(),
					endTime: z.string(),
				}),
			),
		})
		.optional(),
});

export const userAccessApiResponseSchema = createListApiSchema(userAccessSchema).describe('UserAccess');
